/**
 * CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 -- the DOCTOR-SAFE queue context
 * drawer (DOCTOR-CONTEXT LAW).
 *
 * Opened by the doctor's queue/name click on the shared day board -- NEVER
 * the Lead workspace drawer for the doctor role (that read rejects the
 * doctor; the founder's broken-door evidence 2026-07-09/10). Renders the
 * doctor-safe drawer read ONLY: appointment facts, honest identity (name or
 * pending-reason), visit + plan summary, payment-handoff state, and the
 * case-sheet door. ZERO phone, ZERO marketing/attribution, ZERO owner
 * tools -- the server ships nothing else (fenced read), and this component
 * renders only what it is given.
 *
 * The safe-lead-context block renders ONLY when the org's
 * doctor.context_visibility row grants it (the response carries the block
 * only then -- structurally absent at the seeded default).
 *
 * Anatomy: the ONE DrawerShell primitive + the established quick-view fact
 * rows (design-language 03 drawer anatomy; no parallel drawer class family).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchDoctorQueueContext, type DoctorQueueContext } from '@/dashboard/api';
import { AcBadge, AcButton, DrawerShell } from '@/dashboard/ui';
import { formatIstTime } from '@/lib/datetime-ist';
import {
  consultDoorForRow,
  consultPathForAppointment,
  consultRowHint,
} from '@/dashboard/doctor/doctor-home-view';
import {
  drawerArrivedAt,
  drawerHandoffDisplay,
  drawerPendingLine,
  drawerPlanLine,
  drawerTitle,
  drawerVisitChip,
} from '@/dashboard/doctor/doctor-context-view';
import type { TodayAppointment } from '@/dashboard/types';

type DrawerState =
  | { kind: 'loading' }
  | { kind: 'ready'; ctx: DoctorQueueContext }
  | { kind: 'error' };

function FactRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="lead-quickview__fact">
      <span className="lead-quickview__fact-label">{label}</span>
      <span className="lead-quickview__fact-value">{value}</span>
    </div>
  );
}

export interface DoctorQueueContextDrawerProps {
  readonly appointmentId: number;
  /** Seed title while loading (the row's label -- honest, already on screen). */
  readonly seedTitle: string;
  readonly onClose: () => void;
}

export default function DoctorQueueContextDrawer({
  appointmentId,
  seedTitle,
  onClose,
}: DoctorQueueContextDrawerProps): JSX.Element {
  const [state, setState] = useState<DrawerState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchDoctorQueueContext(appointmentId)
      .then((ctx) => {
        if (!cancelled) setState({ kind: 'ready', ctx });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId, reloadKey]);

  const ctx = state.kind === 'ready' ? state.ctx : null;

  // The consult door reuses the queue's exact door semantics (ONE law).
  const doorRow =
    ctx === null
      ? null
      : ({
          id: ctx.appointment.id,
          status: ctx.appointment.status,
          visitStatus: ctx.visit.visitStatus,
        } as unknown as TodayAppointment);
  const door = doorRow === null ? null : consultDoorForRow(doorRow);
  const doorHint = doorRow === null ? null : consultRowHint(doorRow);

  const footer = (
    <>
      {door !== null && ctx !== null ? (
        <AcButton
          size="sm"
          variant={door.emphasis === 'primary' ? 'primary' : 'secondary'}
          onClick={() => navigate(consultPathForAppointment(ctx.appointment.id))}
          data-testid="doctor-drawer-consult-door"
        >
          {door.label}
        </AcButton>
      ) : doorHint !== null ? (
        <span className="lead-quickview__readonly" data-testid="doctor-drawer-door-hint">
          {doorHint}
        </span>
      ) : null}
    </>
  );

  return (
    <DrawerShell
      eyebrow="Patient context"
      title={ctx !== null ? drawerTitle(ctx) : seedTitle}
      onClose={onClose}
      footer={footer}
      testId="doctor-queue-drawer"
    >
      {state.kind === 'loading' ? (
        // The visual-spec loading state: three quiet skeleton strips (the
        // existing .cc-skel idiom; adversarial finding F5).
        <div
          className="doctor-drawer__skel"
          role="status"
          aria-label="Loading patient context"
        >
          <div className="cc-skel cc-skel--strip" />
          <div className="cc-skel cc-skel--strip" />
          <div className="cc-skel cc-skel--strip" />
        </div>
      ) : state.kind === 'error' ? (
        <div className="workboard__feedback workboard__feedback--error" role="alert">
          <p>The patient context could not load right now.</p>
          <AcButton
            size="sm"
            variant="secondary"
            onClick={() => setReloadKey((k) => k + 1)}
            data-testid="doctor-drawer-retry"
          >
            Retry
          </AcButton>
        </div>
      ) : ctx !== null ? (
        <>
          {/* Identity honesty: the pending REASON, never a blank or a
              fabricated name (identity-or-reason law). */}
          {ctx.identity.namePending ? (
            <p
              className="lead-quickview__readonly"
              data-testid="doctor-drawer-pending-reason"
            >
              {drawerPendingLine(ctx)}
            </p>
          ) : null}

          <div className="lead-quickview__facts" data-testid="doctor-drawer-facts">
            <FactRow
              label="Time"
              value={formatIstTime(ctx.appointment.appointmentTime) || null}
            />
            {/* ARRIVAL TRUTH (Scope 2a): the ACTUAL arrival moment as its own
                fact next to the scheduled slot -- two distinct truths. */}
            {drawerArrivedAt(ctx) !== null ? (
              <FactRow
                label="Arrived"
                value={
                  <span data-testid="doctor-drawer-arrived-at">
                    {formatIstTime(drawerArrivedAt(ctx) as string) || null}
                  </span>
                }
              />
            ) : null}
            <FactRow label="Clinic" value={ctx.appointment.clinicName} />
            <FactRow label="Service" value={ctx.appointment.specialty} />
            <FactRow
              label="Visit"
              value={
                <AcBadge tone={drawerVisitChip(ctx).tone}>
                  {drawerVisitChip(ctx).label}
                </AcBadge>
              }
            />
            <FactRow label="Treatment plan" value={drawerPlanLine(ctx)} />
            {(() => {
              const handoff = drawerHandoffDisplay(ctx);
              if (handoff === null) return null;
              return (
                <FactRow
                  label="Payment"
                  value={
                    <span data-testid="doctor-drawer-handoff">
                      <AcBadge tone={handoff.tone}>{handoff.label}</AcBadge>
                      {handoff.detail !== null ? (
                        <span className="lead-quickview__fact-detail">
                          {handoff.detail}
                        </span>
                      ) : null}
                    </span>
                  }
                />
              );
            })()}
          </div>

          {/* SAFE lead context -- renders ONLY when the org's visibility row
              grants it (the read ships the block only then). */}
          {ctx.leadContext !== undefined ? (
            <div
              className="lead-quickview__facts"
              data-testid="doctor-drawer-lead-context"
            >
              <span className="lead-quickview__block-label">Enquiry context</span>
              <FactRow
                label="Patient record"
                value={
                  ctx.leadContext.patientLinked ? (
                    <AcBadge tone="positive">
                      Linked{ctx.leadContext.umr !== null ? ` - ${ctx.leadContext.umr}` : ''}
                    </AcBadge>
                  ) : (
                    <AcBadge tone="pool">Not registered yet</AcBadge>
                  )
                }
              />
              <FactRow label="Enquiry" value={ctx.leadContext.enquiryChannel} />
              <FactRow
                label="Enquiry date"
                value={
                  ctx.leadContext.enquiryDate !== null
                    ? new Date(ctx.leadContext.enquiryDate).toLocaleDateString(
                        'en-IN',
                        { day: 'numeric', month: 'short', year: 'numeric' },
                      )
                    : null
                }
              />
            </div>
          ) : null}
        </>
      ) : null}
    </DrawerShell>
  );
}
